import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { CREATE_CONTENT_REPORT, type ReportEntityKind } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorBox, TextArea } from "./ui";

const MIN_LEN = 10;
const MAX_LEN = 2000;

/**
 * Small flag button that opens an inline report dialog. Hidden for anonymous
 * visitors (reporting is a write; browsing stays open) and on your own person
 * page — reporting yourself is never useful.
 */
export function ReportButton({ entityKind, entityId }: { entityKind: ReportEntityKind; entityId: string }) {
  const { personId } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);

  if (!personId || (entityKind === "PERSON" && entityId === personId)) return null;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title={t("report.button")}
        aria-label={t("report.button")}
        onClick={() => setOpen(true)}
        className="text-muted-foreground hover:text-destructive"
      >
        <Flag aria-hidden className="size-4" />
      </Button>
      <ReportDialog
        open={open}
        onOpenChange={setOpen}
        entityKind={entityKind}
        entityId={entityId}
        reporterId={personId}
      />
    </>
  );
}

function ReportDialog({
  open,
  onOpenChange,
  entityKind,
  entityId,
  reporterId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityKind: ReportEntityKind;
  entityId: string;
  reporterId: string;
}) {
  const { t } = useT();
  const [body, setBody] = useState("");

  const submit = useMutation({
    mutationFn: () => gqlRequest(CREATE_CONTENT_REPORT, { reporterId, entityKind, entityId, body: body.trim() }),
  });

  const length = body.trim().length;
  const valid = length >= MIN_LEN && length <= MAX_LEN;
  const onClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-label={t("report.dialogAria")} className="shadow-pop sm:max-w-md">
        {submit.isSuccess ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground">{t("report.thanks")}</p>
            <Button type="button" onClick={onClose}>
              {t("common.close")}
            </Button>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && !submit.isPending) submit.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>{t("report.heading")}</DialogTitle>
              <p className="text-sm text-muted-foreground">{t("report.intro")}</p>
            </DialogHeader>
            <TextArea
              autoFocus
              rows={4}
              value={body}
              maxLength={MAX_LEN}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("report.placeholder")}
            />
            <p className={`text-xs ${valid || length === 0 ? "text-muted-foreground" : "text-destructive"}`}>
              {length < MIN_LEN
                ? t("report.minChars", { count: length, min: MIN_LEN })
                : t("report.chars", { count: length, max: MAX_LEN })}
            </p>
            {submit.error != null && <ErrorBox error={submit.error} />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={!valid || submit.isPending}>
                {submit.isPending ? t("report.busy") : t("report.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
